import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { authAPI } from '../services/api';
import { decodeTokenExpiration, persistToken } from '../utils/tokenUtils';

export const useAuthStore = create(
    persist(
        (set, get) => ({
            // State
            user: null,
            token: null,
            tokenExpiresAt: null,
            sessionStartedAt: null,
            isAuthenticated: false,
            isLoading: false,
            error: null,

            // Actions
            login: async (credentials) => {
                set({ isLoading: true, error: null });

                try {
                    const response = await authAPI.login(credentials);
                    const { token, user } = response;

                    if (!token || !user) {
                        throw new Error('Invalid login response from server');
                    }

                    const tokenExpiresAt = decodeTokenExpiration(token);
                    const sessionStartedAt = Date.now();

                    // Store token and user data in localStorage
                    persistToken(token, tokenExpiresAt);
                    localStorage.setItem('user', JSON.stringify(user));
                    localStorage.setItem('sessionStartedAt', String(sessionStartedAt));

                    set({
                        user,
                        token,
                        tokenExpiresAt,
                        sessionStartedAt,
                        isAuthenticated: true,
                        isLoading: false,
                        error: null,
                    });

                    return { success: true };
                } catch (error) {
                    set({
                        user: null,
                        token: null,
                        tokenExpiresAt: null,
                        sessionStartedAt: null,
                        isAuthenticated: false,
                        isLoading: false,
                        error: error.message,
                    });

                    return { success: false, error: error.message };
                }
            },

            register: async (userData) => {
                set({ isLoading: true, error: null });

                try {
                    const response = await authAPI.register(userData);
                    const { token, user } = response;

                    if (!token || !user) {
                        throw new Error('Invalid registration response from server');
                    }

                    const tokenExpiresAt = decodeTokenExpiration(token);
                    const sessionStartedAt = Date.now();

                    // Store token and user data in localStorage
                    persistToken(token, tokenExpiresAt);
                    localStorage.setItem('user', JSON.stringify(user));
                    localStorage.setItem('sessionStartedAt', String(sessionStartedAt));

                    set({
                        user,
                        token,
                        tokenExpiresAt,
                        sessionStartedAt,
                        isAuthenticated: true,
                        isLoading: false,
                        error: null,
                    });

                    return { success: true };
                } catch (error) {
                    set({
                        user: null,
                        token: null,
                        tokenExpiresAt: null,
                        sessionStartedAt: null,
                        isAuthenticated: false,
                        isLoading: false,
                        error: error.message,
                    });

                    return { success: false, error: error.message };
                }
            },

            logout: async (skipBackendCall = false) => {
                try {
                    // Try to call backend logout endpoint unless skipped (e.g., for token expiration)
                    if (!skipBackendCall) {
                        await authAPI.logout();
                    }
                } catch (error) {
                    // If backend logout fails, just log the error
                    // Frontend logout should still proceed
                    console.error('Backend logout error:', error);
                } finally {
                    // Always clear frontend state regardless of backend response
                    // This ensures the user is logged out on the frontend
                    localStorage.removeItem('authToken');
                    localStorage.removeItem('user');
                    localStorage.removeItem('tokenExpiresAt');
                    localStorage.removeItem('sessionStartedAt');

                    set({
                        user: null,
                        token: null,
                        tokenExpiresAt: null,
                        sessionStartedAt: null,
                        isAuthenticated: false,
                        isLoading: false,
                        error: null,
                    });
                }
            },

            refreshToken: async () => {
                try {
                    const response = await authAPI.refreshToken();
                    const { token } = response;

                    if (!token) {
                        throw new Error('No token returned from refresh');
                    }

                    const tokenExpiresAt = decodeTokenExpiration(token);

                    persistToken(token, tokenExpiresAt);
                    set({ token, tokenExpiresAt, isAuthenticated: true });

                    return true;
                } catch (error) {
                    await get().logout(true);
                    return false;
                }
            },

            getCurrentUser: async () => {
                set({ isLoading: true });

                try {
                    const user = await authAPI.getProfile();

                    // Store user data in localStorage
                    localStorage.setItem('user', JSON.stringify(user));

                    set({
                        user,
                        isAuthenticated: true,
                        isLoading: false,
                        error: null,
                    });

                    return user;
                } catch (error) {
                    set({
                        user: null,
                        isAuthenticated: false,
                        isLoading: false,
                        error: error.message,
                    });

                    return null;
                }
            },

            // Background auth check that doesn't show loading spinner
            refreshCurrentUser: async () => {
                try {
                    const user = await authAPI.getProfile();

                    // Store user data in localStorage
                    localStorage.setItem('user', JSON.stringify(user));

                    set({
                        user,
                        isAuthenticated: true,
                        error: null,
                    });

                    return user;
                } catch (error) {
                    // Don't clear auth state on background refresh failure
                    // Just log the error
                    console.warn('Background user refresh failed:', error);
                    throw error;
                }
            },

            clearError: () => set({ error: null }),

            // Utility functions
            // ====================== ROLE CHECKS ======================

            hasRole: (role) => {
                const { user } = get();
                if (!user) return false;

                const rolesArray = Array.isArray(user.roles)
                    ? user.roles.map(r => r.toUpperCase())
                    : [];

                const hasFlag = (flag) => {
                    const cap = flag.charAt(0).toUpperCase() + flag.slice(1);
                    return Boolean(user[flag] || user[`is${cap}`]);
                };

                const hasRoleInArray = (roleName) => rolesArray.includes(roleName.toUpperCase());

                // Handle both legacy single role and new multi-role system
                if (user.role) {
                    return user.role === role;
                }

                // New multi-role system
                const roleName = role.toUpperCase();
                switch (roleName) {
                    case 'ADMIN': return hasFlag('admin') || hasRoleInArray('ADMIN');
                    case 'PLANNER': return hasFlag('planner') || hasRoleInArray('PLANNER');
                    case 'PROMOTER': return hasFlag('promoter') || hasRoleInArray('PROMOTER');
                    case 'STAFF': return hasFlag('staff') || hasRoleInArray('STAFF');
                    case 'BUYER': return hasFlag('buyer') || hasRoleInArray('BUYER');
                    case 'SALE_AGENT': return hasFlag('saleAgent') || hasRoleInArray('SALE_AGENT');
                    default: return false;
                }
            },

            hasAnyRole: (roles) => {
                if (!roles || !Array.isArray(roles)) return false;
                return roles.some(role => get().hasRole(role));
            },

            getUserRoles: () => {
                const { user } = get();
                if (!user) return [];

                // Handle legacy single role
                if (user.role) {
                    return [user.role];
                }

                const roles = new Set();
                if (Array.isArray(user.roles)) {
                    user.roles.forEach(r => roles.add(r.toUpperCase()));
                }
                if (user.isAdmin || user.admin) roles.add('ADMIN');
                if (user.isPlanner || user.planner) roles.add('PLANNER');
                if (user.isPromoter || user.promoter) roles.add('PROMOTER');
                if (user.isStaff || user.staff) roles.add('STAFF');
                if (user.isSaleAgent || user.saleAgent) roles.add('SALE_AGENT');
                if (user.isBuyer || user.buyer) roles.add('BUYER');
                return Array.from(roles);
            },

            getPrimaryRole: () => {
                const { user } = get();
                if (!user) return null;

                // Handle legacy single role
                if (user.role) {
                    return user.role;
                }

                // New multi-role system - priority order
                if (user.isAdmin || user.admin) return 'ADMIN';
                if (user.isPlanner || user.planner) return 'PLANNER';
                if (user.isPromoter || user.promoter) return 'PROMOTER';
                if (user.isStaff || user.staff) return 'STAFF';
                if (user.isSaleAgent || user.saleAgent) return 'SALE_AGENT';
                if (user.isBuyer || user.buyer) return 'BUYER';
                return null;
            },

            // ====================== CONVENIENCE ROLE CHECKS ======================

            isAdmin: () => get().hasRole('ADMIN'),
            isPlanner: () => get().hasRole('PLANNER'),
            isPromoter: () => get().hasRole('PROMOTER'),
            isStaff: () => get().hasRole('STAFF'),
            isSaleAgent: () => get().hasRole('SALE_AGENT'),
            isBuyer: () => get().hasRole('BUYER'),


            // ====================== PERMISSION CHECKS ======================

            // Administrative Permissions
            canManageSystem: () => get().hasRole('ADMIN'),
            canManageUsers: () => get().hasRole('ADMIN'),
            canViewSystemAnalytics: () => get().hasAnyRole(['ADMIN', 'PLANNER']),
            canManageSystemSettings: () => get().hasRole('ADMIN'),

            // Venue Management
            canCreateVenues: () => get().hasAnyRole(['ADMIN', 'PLANNER']),
            canManageVenues: () => get().hasAnyRole(['ADMIN', 'PLANNER']),
            canManageVenueLayouts: () => get().hasAnyRole(['ADMIN', 'PLANNER']),

            // Event Management
            canCreateEvents: () => get().hasAnyRole(['ADMIN', 'PLANNER', 'PROMOTER']),
            canManageEvents: () => get().hasAnyRole(['ADMIN', 'PLANNER', 'PROMOTER']),
            canManageEventPricing: () => get().hasAnyRole(['ADMIN', 'PLANNER', 'PROMOTER']),
            canPublishEvents: () => get().hasAnyRole(['ADMIN', 'PLANNER']),

            // Ticket Management
            canViewAllTicketSales: () => get().hasAnyRole(['ADMIN', 'PLANNER']),
            canManageTicketInventory: () => get().hasAnyRole(['ADMIN', 'PLANNER', 'PROMOTER']),
            canProcessRefunds: () => get().hasAnyRole(['ADMIN', 'PLANNER', 'STAFF']),
            canScanTickets: () => get().hasAnyRole(['ADMIN', 'PLANNER', 'PROMOTER', 'STAFF']),
            canValidateTickets: () => get().hasAnyRole(['ADMIN', 'PLANNER', 'PROMOTER', 'STAFF']),

            // Customer Service
            canViewSupportRequests: () => get().hasAnyRole(['ADMIN', 'STAFF']),
            canProvideSupportResponse: () => get().hasAnyRole(['ADMIN', 'STAFF']),
            canAccessCustomerAccounts: () => get().hasAnyRole(['ADMIN', 'STAFF']),

            // Financial
            canViewFinancialReports: () => get().hasAnyRole(['ADMIN', 'PLANNER']),
            canManagePaymentSettings: () => get().hasRole('ADMIN'),
            canViewPayouts: () => get().hasAnyRole(['ADMIN', 'PLANNER']),

            // Promotions
            canCreatePromoCodes: () => get().hasAnyRole(['ADMIN', 'PLANNER', 'PROMOTER']),
            canManagePromotions: () => get().hasAnyRole(['ADMIN', 'PLANNER', 'PROMOTER']),
            canViewEventAnalytics: () => get().hasAnyRole(['ADMIN', 'PLANNER', 'PROMOTER']),

            // Buyer/Customer
            canPurchaseTickets: () => get().isAuthenticated,
            canViewOwnPurchases: () => get().isAuthenticated,
            canCancelOwnOrders: () => get().isAuthenticated,

            // Data Access
            canExportData: () => get().hasAnyRole(['ADMIN', 'PLANNER']),

            // Admin Dashboard Access
            canAccessAdmin: () => get().hasRole('ADMIN'),
            canAccessPlannerDashboard: () => get().hasAnyRole(['ADMIN', 'PLANNER']),
            canAccessPromoterDashboard: () => get().hasAnyRole(['ADMIN', 'PLANNER', 'PROMOTER']),

            // UI Helper Functions
            shouldShowAdminNavigation: () => get().hasRole('ADMIN'),
            shouldShowPlannerNavigation: () => get().hasAnyRole(['ADMIN', 'PLANNER']),
            shouldShowPromoterNavigation: () => get().hasAnyRole(['ADMIN', 'PLANNER', 'PROMOTER']),
            shouldShowStaffNavigation: () => get().hasAnyRole(['ADMIN', 'PLANNER', 'PROMOTER', 'STAFF']),
        }),
        {
            name: 'auth-storage',
            partialize: (state) => ({
                user: state.user,
                token: state.token,
                sessionStartedAt: state.sessionStartedAt,
                isAuthenticated: state.isAuthenticated,
            }),
        }
    )
);
